import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Home, LogOut, Bell, User, FileText, IdCard, Award, FileBadge2, Mail, Stamp,
  GraduationCap, BadgeCheck, Download, CreditCard, Calendar, TrendingUp,
  CheckCircle2, Check, Clock, Settings, Menu, X, ChevronRight, QrCode, ArrowRight,
  ShoppingBag, Play, Star, Lock, FileQuestion, ArrowLeft, Sparkles, Search, Filter, PlayCircle, Loader2,
  CalendarDays, MessageSquare, Mails, Newspaper, Users2, Gift, BarChart3, BookOpen, Crown, Camera, Receipt, Scale, Building2, Video,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Input } from "../components/ui/input";
import { LOGO_URL } from "../data/mock";
import {
  MOCK_USER, MOCK_DOCUMENTS,
} from "../data/dashboardMock";
import {
  EventiTab, ForumTab, MessaggiTab, NewsTab, DirectoryTab, ConvenzioniTab, StatistichePersonaliTab,
} from "./DashboardTabs";
import QuizModal from "../components/QuizModal";
import ForceChangePasswordModal from "../components/ForceChangePasswordModal";
import PaywallScreen from "../components/PaywallScreen";
import AppuntamentoAvvocatoSection from "../components/AppuntamentoAvvocatoSection";
import { QRCodeSVG } from "qrcode.react";
import MaterialeProTab from "./MaterialeProTab";
import FormazioneACDT from "./FormazioneACDT";
import GuideQuotidiane from "./GuideQuotidiane";
import ProcedureMimitSection from "./ProcedureMimitSection";
import SfondiMeetingSection from "./SfondiMeetingSection";
import LeMieFatture from "./LeMieFatture";
import TesseraReferralSection from "./TesseraReferralSection";
import AITutorSection from "./AITutorSection";
import OnboardingModal from "../components/OnboardingModal";

const API = process.env.REACT_APP_BACKEND_URL;

function formatTimeAgo(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "ora";
  if (m < 60) return `${m} min fa`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} ore fa`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d} giorn${d === 1 ? "o" : "i"} fa`;
  return new Date(iso).toLocaleDateString("it-IT");
}

function adaptUser(apiUser) {
  // Map snake_case backend → camelCase per compatibilità con mock + computed fields
  const iso = apiUser.iscritto_dal;
  // Scadenza: 1 anno dopo iscritto_dal (default)
  let scadenza = apiUser.scadenza_tesseramento;
  if (!scadenza && iso) {
    const d = new Date(iso);
    d.setFullYear(d.getFullYear() + 1);
    scadenza = d.toISOString();
  }
  const giorniRimanenti = scadenza
    ? Math.max(0, Math.ceil((new Date(scadenza) - new Date()) / 86400000))
    : 365;
  const achievements = [];
  if (iso) achievements.push({ id: "tesserato", t: "Tesserato A.C.D.T.", date: iso, icon: "BadgeCheck" });
  if (apiUser.corso_base_completato) achievements.push({ id: "corso-base", t: "Corso Base Completato", date: iso, icon: "GraduationCap" });
  if (apiUser.iscritto_registro) achievements.push({ id: "registro", t: "Iscritto al Registro Nazionale", date: iso, icon: "Award" });
  return {
    ...MOCK_USER,
    id: apiUser.id,
    nome: apiUser.nome,
    cognome: apiUser.cognome,
    email: apiUser.email,
    telefono: apiUser.telefono || MOCK_USER.telefono,
    numeroTessera: apiUser.numero_tessera || MOCK_USER.numeroTessera,
    iscrittoDal: iso || MOCK_USER.iscrittoDal,
    scadenzaTesseramento: scadenza || MOCK_USER.scadenzaTesseramento,
    giorniRimanenti,
    statoTesseramento: "attivo",
    cfdtAnno: apiUser.cfdt_anno ?? 0,
    cfdtRichiesti: apiUser.cfdt_richiesti ?? 20,
    pacchetto: apiUser.pacchetto || "Base",
    iscrittoRegistro: apiUser.iscritto_registro || false,
    corsoBaseCompletato: apiUser.corso_base_completato || false,
    role: apiUser.role,
    citta: apiUser.citta,
    provincia: apiUser.provincia,
    specializzazione: apiUser.specializzazione,
    onboardingCompletato: !!apiUser.onboarding_completato,
    tipoSocio: apiUser.tipo_socio || "standard",
    must_change_password: !!apiUser.must_change_password,
    accesso_pieno: !!apiUser.accesso_pieno,
    ruolo_direttivo: apiUser.ruolo_direttivo || null,
    corso_sbloccato_dts: !!apiUser.corso_sbloccato_dts,
    corso_sbloccato_legacy: !!apiUser.corso_sbloccato_legacy,
    fotoTesseraId: apiUser.foto_tessera_id || null,
    achievements: achievements.length ? achievements : MOCK_USER.achievements,
  };
}

const ICONS = { FileText, IdCard, Award, FileBadge2, Mail, Stamp, BadgeCheck, GraduationCap, TrendingUp, BookOpen, Crown, FileQuestion };

function SidebarSection({ label }) {
  return (
    <div className="px-3 pt-3 pb-1 text-[10px] font-extrabold uppercase tracking-[0.15em] text-slate-400">{label}</div>
  );
}

function renderNavItem(item, tab, switchTab) {
  const I = item.icon;
  const active = tab === item.id;
  return (
    <button
      key={item.id}
      data-testid={`nav-${item.id}`}
      onClick={() => switchTab(item.id)}
      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-[13.5px] font-semibold transition-colors ${active ? "bg-[#102a6b] text-white" : "text-slate-600 hover:bg-slate-100 hover:text-[#102a6b]"}`}
    >
      <I className="h-4 w-4" />
      <span className="flex-1 text-left">{item.label}</span>
      {item.badge && (
        <span className={`text-[9.5px] px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wider ${active ? "bg-[#f59e0b] text-[#0a1f55]" : "bg-[#f59e0b]/15 text-[#b45309]"}`}>{item.badge}</span>
      )}
    </button>
  );
}

export default function DashboardSoci() {
  const [tab, setTab] = useState("home");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeCorso, setActiveCorso] = useState(null);
  const [user, setUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/api/auth/me`, { withCredentials: true })
      .then((r) => setUser(adaptUser(r.data)))
      .catch(() => navigate("/area-soci"))
      .finally(() => setLoadingUser(false));
  }, [navigate]);

  useEffect(() => {
    if (!user) return undefined;
    let mounted = true;
    const fetchCount = () => {
      axios.get(`${API}/api/me/messaggi/non-letti/count`, { withCredentials: true })
        .then((r) => { if (mounted) setUnreadCount(r.data?.count || 0); })
        .catch(() => {});
    };
    fetchCount();
    const id = setInterval(fetchCount, 20000);
    return () => { mounted = false; clearInterval(id); };
  }, [user]);

  const handleLogout = async () => {
    try { await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true }); } catch (_e) { /* ignore */ }
    sessionStorage.removeItem("acdt_user");
    navigate("/area-soci");
  };

  if (loadingUser || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f5f7ff]">
        <Loader2 className="h-8 w-8 animate-spin text-[#102a6b]" />
      </div>
    );
  }

  const cfdtPercent = Math.round((user.cfdtAnno / user.cfdtRichiesti) * 100);

  const accessibleDocs = MOCK_DOCUMENTS.filter((d) => {
    if (d.scope === "tutti") return true;
    if (d.scope === "corso") return user.corsoBaseCompletato || user.cfdtAnno > 0;
    if (d.scope === "registro") return user.iscrittoRegistro;
    return false;
  });

  const switchTab = (id) => { setTab(id); setActiveCorso(null); setSidebarOpen(false); };

  const handleOnboardingComplete = () => {
    // Ricarica i dati utente per chiudere il modal e mostrare la dashboard
    axios.get(`${API}/api/auth/me`, { withCredentials: true })
      .then((r) => setUser(adaptUser(r.data)))
      .catch(() => {});
  };

  // PAYWALL — Blocca l'accesso dashboard finché la tessera non è pagata.
  // Bypass automatico per: admin, membri del Direttivo, e utenti guest (checkout in corso).
  const needsPaywall =
    user.role !== "admin" &&
    !user.ruolo_direttivo &&
    user.tipoSocio !== "guest" &&
    !user.accesso_pieno;
  if (needsPaywall) {
    return <PaywallScreen user={user} onLogout={handleLogout} />;
  }

  // Modal di onboarding obbligatorio al primo accesso (solo user, non admin, non guest)
  if (user.role !== "admin" && user.tipoSocio !== "guest" && !user.onboardingCompletato) {
    return <OnboardingModal user={user} onComplete={handleOnboardingComplete} />;
  }

  const isGuest = user.tipoSocio === "guest";

  return (
    <div className="min-h-screen bg-[#f5f7ff] flex">
      {user.must_change_password && (
        <ForceChangePasswordModal
          onSuccess={() => setUser({ ...user, must_change_password: false })}
        />
      )}
      {/* Sidebar */}
      <aside data-testid="dashboard-sidebar" className={`fixed lg:sticky top-0 left-0 z-40 h-screen w-72 bg-white border-r border-slate-200 transition-transform lg:translate-x-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="p-5 border-b border-slate-200 flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center">
            <img src={LOGO_URL} alt="ACDT" className="h-9 w-9 object-contain" />
          </div>
          <div>
            <div className="font-extrabold text-[#0a1f55]">A.C.D.T.</div>
            <div className="text-[10.5px] uppercase tracking-wider text-slate-500">Area Soci</div>
          </div>
          <button className="lg:hidden ml-auto" onClick={() => setSidebarOpen(false)}><X className="h-5 w-5" /></button>
        </div>

        <nav className="p-3 space-y-1 overflow-y-auto" style={{ maxHeight: "calc(100vh - 220px)" }}>
          <SidebarSection label="Account" />
          {[
            { id: "home", icon: Home, label: "Panoramica" },
            { id: "tessera", icon: IdCard, label: "Tessera Digitale" },
            { id: "fatture", icon: Receipt, label: "Le mie fatture" },
            { id: "referral", icon: Gift, label: "Invita amici", badge: "€10" },
            { id: "profilo", icon: User, label: "Profilo" },
          ].map((item) => renderNavItem(item, tab, switchTab))}

          <SidebarSection label="Formazione" />
          {[
            { id: "formazione-acdt", icon: GraduationCap, label: "Corso A.C.D.T.", badge: "Ufficiale" },
            { id: "guide-quotidiane", icon: BookOpen, label: "Guide Quotidiane", badge: "Nuovo" },
            { id: "procedure-mimit", icon: Building2, label: "Procedure Agg. MIMIT", badge: "Nuovo" },
            { id: "sfondi-meeting", icon: Video, label: "Sfondi Meeting", badge: "Nuovo" },
            { id: "appuntamento-avvocato", icon: Scale, label: "Appuntamento Avvocato", badge: "Gratis" },
            { id: "ai-tutor", icon: Sparkles, label: "AI Tutor", badge: "AI" },
            { id: "cfdt", icon: TrendingUp, label: "Crediti CFDT" },
            { id: "corsi", icon: GraduationCap, label: "I miei Corsi" },
            { id: "catalogo", icon: ShoppingBag, label: "Catalogo Corsi", badge: "Nuovo" },
            { id: "specializzazioni", icon: Sparkles, label: "Specializzazioni", badge: "In arrivo" },
            { id: "eventi", icon: CalendarDays, label: "Eventi & Calendario" },
            { id: "stats", icon: BarChart3, label: "Statistiche" },
          ].map((item) => renderNavItem(item, tab, switchTab))}

          <SidebarSection label="Community" />
          {[
            { id: "bacheca", icon: Newspaper, label: "Bacheca Soci", badge: "Novità" },
            { id: "forum", icon: MessageSquare, label: "Forum" },
            { id: "messaggi", icon: Mails, label: "Messaggi", badge: unreadCount > 0 ? String(unreadCount) : null },
            { id: "directory", icon: Users2, label: "Directory Soci" },
          ].map((item) => renderNavItem(item, tab, switchTab))}

          <SidebarSection label="Risorse" />
          {[
            { id: "news", icon: Newspaper, label: "News" },
            { id: "convenzioni", icon: Gift, label: "Convenzioni" },
            { id: "docs", icon: FileText, label: "Documenti" },
            { id: "materiale-pro", icon: Stamp, label: "Materiale Pro" },
            { id: "pagamenti", icon: CreditCard, label: "Pagamenti" },
          ].map((item) => renderNavItem(item, tab, switchTab))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-slate-200 space-y-1">
          {user?.role === "admin" && (
            <Link
              to="/admin"
              data-testid="dashboard-admin-link"
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13px] font-semibold text-[#f59e0b] hover:bg-[#f59e0b]/10"
            >
              <Settings className="h-4 w-4" /> Pannello Admin
            </Link>
          )}
          <button onClick={handleLogout} data-testid="logout-btn" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-[14px] font-semibold text-slate-600 hover:bg-slate-100">
            <LogOut className="h-4 w-4" /> Esci
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-white/85 backdrop-blur-md border-b border-slate-200 px-5 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" onClick={() => setSidebarOpen(true)}><Menu className="h-5 w-5" /></button>
            <h1 className="text-lg font-extrabold text-[#0a1f55]">Ciao, {user.nome} 👋</h1>
          </div>
          <div className="flex items-center gap-3">
            <NotificheBell count={unreadCount} onClick={() => switchTab("messaggi")} />
            
            <div className="h-9 w-9 rounded-full bg-gradient-to-br from-[#102a6b] to-[#1e40af] text-white flex items-center justify-center font-bold text-[13px]">
              {user.nome[0]}{user.cognome[0]}
            </div>
          </div>
        </header>

        <div className="p-5 md:p-8 space-y-6">
          {isGuest && <GuestUpsellBanner user={user} />}
          {tab === "home" && <HomeTab user={user} cfdtPercent={cfdtPercent} setTab={switchTab} />}
          {tab === "tessera" && <TesseraTab user={user} />}
          {tab === "fatture" && <LeMieFatture />}
          {tab === "referral" && <TesseraReferralSection user={user} />}
          {tab === "ai-tutor" && <AITutorSection />}
          {(tab === "formazione-acdt" || tab === "corso-abilitazione") && <FormazioneACDT user={user} />}
          {tab === "guide-quotidiane" && <GuideQuotidiane />}
          {tab === "procedure-mimit" && <ProcedureMimitSection />}
          {tab === "sfondi-meeting" && <SfondiMeetingSection />}
          {tab === "appuntamento-avvocato" && <AppuntamentoAvvocatoSection />}
          {tab === "cfdt" && <CFDTTab user={user} cfdtPercent={cfdtPercent} />}
          {tab === "corsi" && (activeCorso ? (
            <CorsoPlayer corso={activeCorso} onBack={() => setActiveCorso(null)} />
          ) : (
            <CorsiTab onOpen={(c) => setActiveCorso(c)} />
          ))}
          {tab === "catalogo" && <CatalogoTab user={user} />}
          {tab === "specializzazioni" && <SpecializzazioniTab />}
          {tab === "eventi" && <EventiTab />}
          {tab === "stats" && <StatistichePersonaliTab />}
          {tab === "bacheca" && <BachecaTab user={user} />}
          {tab === "forum" && <ForumTab />}
          {tab === "messaggi" && <MessaggiTab />}
          {tab === "directory" && <DirectoryTab />}
          {tab === "news" && <NewsTab />}
          {tab === "convenzioni" && <ConvenzioniTab />}
          {tab === "docs" && <DocsTab docs={accessibleDocs} />}
          {tab === "materiale-pro" && <MaterialeProTab user={user} />}
          {tab === "pagamenti" && <PagamentiTab />}
          {tab === "profilo" && <ProfiloTab user={user} />}
        </div>
      </main>
    </div>
  );
}

function NotificheBell({ count = 0, onClick }) {
  return (
    <button
      data-testid="navbar-bell-messaggi"
      onClick={onClick}
      aria-label={count > 0 ? `${count} messaggi non letti` : "Nessun messaggio non letto"}
      className="relative h-9 w-9 rounded-lg hover:bg-slate-100 flex items-center justify-center transition-colors"
    >
      <Bell className="h-4 w-4 text-slate-600" />
      {count > 0 && (
        <span
          data-testid="bell-unread-count"
          className="absolute -top-1 -right-1 h-5 min-w-[20px] px-1.5 rounded-full bg-[#f59e0b] text-[#0a1020] text-[10.5px] font-extrabold flex items-center justify-center shadow-sm ring-2 ring-white"
        >
          {count > 99 ? "99+" : count}
        </span>
      )}
    </button>
  );
}

function HomeTab({ user, cfdtPercent, setTab }) {
  const [notifiche, setNotifiche] = useState([]);
  const [badges, setBadges] = useState([]);
  const [bachecaPreview, setBachecaPreview] = useState([]);
  useEffect(() => {
    axios.get(`${API}/api/me/notifiche`, { withCredentials: true })
      .then((r) => setNotifiche(r.data))
      .catch(() => setNotifiche([]));
    axios.get(`${API}/api/me/badges`, { withCredentials: true })
      .then((r) => setBadges(r.data))
      .catch(() => setBadges([]));
    axios.get(`${API}/api/bacheca/posts?limit=3`, { withCredentials: true })
      .then((r) => setBachecaPreview(r.data.posts || []))
      .catch(() => setBachecaPreview([]));
  }, []);
  return (
    <>
      {bachecaPreview.length > 0 && (
        <div className="rounded-2xl bg-gradient-to-r from-[#fef3c7] via-white to-[#fef3c7]/50 border-2 border-[#f59e0b]/40 p-5 md:p-6" data-testid="home-bacheca-widget">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Newspaper className="h-5 w-5 text-[#f59e0b]" />
              <h3 className="font-extrabold text-[#0a1f55] text-lg">Bacheca Soci</h3>
              <span className="text-[10px] uppercase tracking-wider font-extrabold bg-[#f59e0b] text-[#0a1020] px-2 py-0.5 rounded">In evidenza</span>
            </div>
            <button onClick={() => setTab("bacheca")} className="text-[12.5px] font-bold text-[#102a6b] hover:underline" data-testid="bacheca-vedi-tutti">
              Vedi tutti →
            </button>
          </div>
          <div className="space-y-2">
            {bachecaPreview.slice(0, 3).map((p) => (
              <button
                key={p.id}
                onClick={() => setTab("bacheca")}
                className="w-full text-left rounded-lg bg-white border border-slate-200 hover:border-[#102a6b]/40 p-3 transition-colors"
              >
                <div className="flex items-center gap-2 mb-1">
                  {p.tipo === "comunicazione" && <span className="text-[9px] uppercase tracking-wider font-extrabold bg-[#f59e0b] text-[#0a1020] px-1.5 py-0.5 rounded">📢 Direttivo</span>}
                  <span className="text-[11px] text-slate-500">{p.autore_nome} · {new Date(p.created_at).toLocaleDateString("it-IT")}</span>
                </div>
                <div className="font-bold text-[#0a1f55] text-[14px] leading-tight">{p.titolo}</div>
                <div className="text-[12.5px] text-slate-600 line-clamp-1 mt-0.5">{p.contenuto}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-5">
        <StatusCard title="Tesseramento" value="Attivo" desc={`Scade tra ${user.giorniRimanenti} giorni`} icon={BadgeCheck} color="emerald" />
        <StatusCard title="Crediti CFDT" value={`${user.cfdtAnno}/${user.cfdtRichiesti}`} desc={`${cfdtPercent}% del minimo annuale`} icon={TrendingUp} color="blue" />
        <StatusCard title="Pacchetto" value={user.pacchetto} desc="Tutte le funzioni attive" icon={Award} color="amber" />
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 rounded-2xl bg-gradient-to-br from-[#0a1f55] via-[#102a6b] to-[#1e3a8a] text-white p-6 md:p-8 relative overflow-hidden">
          <div className="absolute -top-16 -right-16 h-44 w-44 rounded-full bg-[#f59e0b]/25 blur-3xl" />
          <div className="relative flex items-start justify-between gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#fbbf24]">Tessera A.C.D.T.</div>
              <div className="mt-2 text-2xl font-extrabold">{user.nome} {user.cognome}</div>
              <div className="mt-1 text-[13px] text-slate-200">N. tessera: <strong>{user.numeroTessera}</strong></div>
              <div className="mt-1 text-[13px] text-slate-200">Iscritto dal: {new Date(user.iscrittoDal).toLocaleDateString("it-IT")}</div>
              {user.iscrittoRegistro && (
                <span className="mt-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#f59e0b] text-[#0a1f55] text-[10.5px] font-extrabold uppercase tracking-wider">
                  <Award className="h-3 w-3" /> Iscritto al Registro
                </span>
              )}
            </div>
            <div className="h-16 w-16 rounded-xl bg-white p-1.5">
              <img src={LOGO_URL} alt="ACDT" className="h-full w-full object-contain" />
            </div>
          </div>
          <Button onClick={() => setTab("tessera")} className="relative mt-6 bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1f55] font-bold">
            <QrCode className="mr-2 h-4 w-4" /> Apri tessera digitale
          </Button>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="text-[11px] uppercase tracking-wider font-bold text-[#f59e0b]">Rinnovo</div>
          <h3 className="mt-1 text-lg font-extrabold text-[#0a1f55]">Tesseramento</h3>
          <div className="mt-3 flex items-center gap-2 text-[13px] text-slate-600">
            <Calendar className="h-4 w-4 text-[#102a6b]" />
            Scade il {new Date(user.scadenzaTesseramento).toLocaleDateString("it-IT")}
          </div>
          <div className="mt-4 p-3 rounded-lg bg-[#f59e0b]/10 border border-[#f59e0b]/30 text-[12.5px] text-[#b45309]">
            Quota annuale: <strong>€ 50,00</strong> + eventuale Registro € 69,90
          </div>
          <Button className="mt-4 w-full bg-[#102a6b] hover:bg-[#0a1f55] text-white">
            Rinnova ora <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Highlight catalogo */}
      <div className="rounded-2xl border-2 border-[#f59e0b]/40 bg-gradient-to-br from-[#fff7ed] to-white p-6 flex flex-col md:flex-row items-start md:items-center gap-5">
        <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-[#f59e0b] to-[#d97706] text-white flex items-center justify-center">
          <Sparkles className="h-7 w-7" />
        </div>
        <div className="flex-1">
          <div className="text-[11px] uppercase tracking-wider font-bold text-[#b45309]">Catalogo corsi</div>
          <h3 className="text-lg md:text-xl font-extrabold text-[#0a1f55] mt-0.5">Scopri 6 nuovi corsi disponibili</h3>
          <p className="text-[13.5px] text-slate-600 mt-1">3 sono inclusi nel tuo pacchetto <strong className="text-[#b45309]">Premium</strong> e gratuiti per te.</p>
        </div>
        <Button onClick={() => setTab("catalogo")} className="bg-[#102a6b] hover:bg-[#0a1f55] text-white font-bold">
          Vai al catalogo <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <h3 className="text-lg font-extrabold text-[#0a1f55]">Crediti CFDT</h3>
            <p className="text-[13px] text-slate-500">Ti mancano <strong className="text-[#102a6b]">{user.cfdtRichiesti - user.cfdtAnno} CFDT</strong> per il rinnovo annuale Registro</p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-extrabold text-[#102a6b]">{user.cfdtAnno}/{user.cfdtRichiesti}</div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500">CFDT accumulati</div>
          </div>
        </div>
        <Progress value={cfdtPercent} className="h-3" />
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-extrabold text-[#0a1f55]">I tuoi traguardi</h3>
          <span className="text-[11.5px] text-slate-500 font-bold">
            {badges.filter(b => b.sbloccato).length}/{badges.length} sbloccati
          </span>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {badges.map((b) => {
            const I = ICONS[b.icona] || Award;
            return (
              <div
                key={b.id}
                data-testid={`badge-${b.id}`}
                className={`rounded-xl border p-4 transition-all ${
                  b.sbloccato
                    ? "border-[#f59e0b]/40 bg-gradient-to-br from-[#fff7ed] to-white"
                    : "border-slate-200 bg-slate-50 opacity-70"
                }`}
                title={b.descrizione}
              >
                <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${
                  b.sbloccato
                    ? "bg-gradient-to-br from-[#f59e0b] to-[#d97706] text-white shadow-lg shadow-[#f59e0b]/30"
                    : "bg-slate-200 text-slate-400"
                }`}>
                  <I className="h-5 w-5" />
                </div>
                <div className="mt-3 text-[13px] font-extrabold text-[#0a1f55] leading-tight">{b.nome}</div>
                <div className="text-[11px] text-slate-500 mt-0.5 line-clamp-2 min-h-[2rem]">{b.descrizione}</div>
                {!b.sbloccato && b.obiettivo && (
                  <div className="mt-2 flex items-center gap-2">
                    <div className="flex-1 h-1 rounded-full bg-slate-200 overflow-hidden">
                      <div className="h-full bg-[#f59e0b]" style={{ width: `${(b.progresso / b.obiettivo) * 100}%` }} />
                    </div>
                    <span className="text-[10px] font-bold text-slate-500">{b.progresso}/{b.obiettivo}</span>
                  </div>
                )}
                {b.sbloccato && (
                  <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700">
                    <CheckCircle2 className="h-3 w-3" /> Sbloccato
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <h3 className="text-lg font-extrabold text-[#0a1f55] mb-4">Notifiche recenti</h3>
        {notifiche.length === 0 ? (
          <div className="text-[13px] text-slate-400 text-center py-6">Nessuna notifica al momento</div>
        ) : (
          <div className="space-y-3">
            {notifiche.slice(0, 5).map((n) => (
              <div key={n.id} className={`flex items-start gap-3 p-3 rounded-xl ${n.letta ? "bg-slate-50" : "bg-[#f59e0b]/5 border border-[#f59e0b]/20"}`}>
                <div className={`h-2 w-2 mt-2 rounded-full ${n.letta ? "bg-slate-300" : "bg-[#f59e0b]"}`} />
                <div className="flex-1">
                  <div className="text-[14px] font-bold text-[#0a1f55]">{n.titolo}</div>
                  <div className="text-[12.5px] text-slate-600">{n.descrizione}</div>
                </div>
                <span className="text-[11px] text-slate-400 whitespace-nowrap">{formatTimeAgo(n.created_at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function StatusCard({ title, value, desc, icon: Icon, color }) {
  const colors = {
    emerald: "bg-emerald-500/10 text-emerald-700",
    blue: "bg-[#102a6b]/10 text-[#102a6b]",
    amber: "bg-[#f59e0b]/15 text-[#b45309]",
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${colors[color]}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="mt-3 text-[11.5px] uppercase tracking-wider text-slate-500 font-semibold">{title}</div>
      <div className="text-2xl font-extrabold text-[#0a1f55]">{value}</div>
      <div className="text-[12.5px] text-slate-500 mt-0.5">{desc}</div>
    </div>
  );
}

function TesseraTab({ user }) {
  const verifyUrl = `${window.location.origin}/verifica/${encodeURIComponent(user.numeroTessera)}`;
  const fotoUrl = user.fotoTesseraId ? `${API}/api/files/${user.fotoTesseraId}` : null;
  const fileRef = React.useRef(null);
  const [uploading, setUploading] = React.useState(false);
  const handlePrint = () => { window.print(); };
  const handleScaricaPDF = () => { window.open(`${API}/api/me/tessera.pdf`, "_blank"); };
  const handleFotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) { alert("Max 500KB"); return; }
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      await axios.post(`${API}/api/me/upload-foto-tessera`, fd, {
        withCredentials: true, headers: { "Content-Type": "multipart/form-data" },
      });
      const me = await axios.get(`${API}/api/auth/me`, { withCredentials: true });
      window.location.reload();
      return me;
    } catch (err) {
      alert(err.response?.data?.detail || "Errore upload foto");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };
  return (
    <div className="max-w-xl mx-auto">
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #tessera-printable, #tessera-printable * { visibility: visible !important; }
          #tessera-printable { position: absolute; left: 0; top: 0; width: 100%; padding: 24px; }
          .no-print { display: none !important; }
        }
      `}</style>
      <div id="tessera-printable" data-testid="tessera-digitale" className="rounded-3xl bg-gradient-to-br from-[#0a1f55] via-[#102a6b] to-[#1e3a8a] text-white p-8 relative overflow-hidden shadow-2xl">
        <div className="absolute -top-16 -right-16 h-44 w-44 rounded-full bg-[#f59e0b]/25 blur-3xl no-print" />
        <div className="relative">
          <div className="flex items-center justify-between">
            <div className="h-14 w-14 rounded-xl bg-white p-1.5"><img src={LOGO_URL} alt="ACDT" className="h-full w-full object-contain" /></div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#fbbf24]">A.C.D.T.</div>
              <div className="text-[10px] text-slate-300">Tessera digitale</div>
            </div>
          </div>
          <div className="mt-7 flex items-start gap-4">
            {fotoUrl && (
              <div data-testid="tessera-foto" className="h-24 w-20 rounded-lg overflow-hidden bg-white border-2 border-[#f59e0b]/50 flex-shrink-0 shadow-md">
                <img src={fotoUrl} alt={`${user.nome} ${user.cognome}`} className="w-full h-full object-cover" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-2xl font-extrabold leading-tight">{user.nome} {user.cognome}</div>
              <div className="mt-1 text-slate-300 text-[12.5px] truncate">{user.email}</div>
              {user.iscrittoRegistro && (
                <div className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#f59e0b]/20 text-[#fbbf24] text-[10px] font-extrabold uppercase tracking-wider">
                  Iscritto al Registro
                </div>
              )}
            </div>
          </div>
          <div className="mt-6 grid grid-cols-2 gap-4 text-[12.5px]">
            <div><div className="text-slate-400 uppercase tracking-wider text-[10px]">N. Tessera</div><div data-testid="tessera-numero" className="font-bold">{user.numeroTessera}</div></div>
            <div><div className="text-slate-400 uppercase tracking-wider text-[10px]">Pacchetto</div><div className="font-bold capitalize">{user.pacchetto}</div></div>
            <div><div className="text-slate-400 uppercase tracking-wider text-[10px]">Iscritto dal</div><div className="font-bold">{new Date(user.iscrittoDal).toLocaleDateString("it-IT")}</div></div>
            <div><div className="text-slate-400 uppercase tracking-wider text-[10px]">Scadenza</div><div className="font-bold">{new Date(user.scadenzaTesseramento).toLocaleDateString("it-IT")}</div></div>
          </div>
          <div className="mt-6 pt-6 border-t border-white/15 flex items-center justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.18em] font-extrabold text-[#fbbf24]">Verifica autenticità</div>
              <div className="mt-1 text-[11.5px] text-slate-200 leading-snug">
                Scansiona il QR code per verificare l&apos;iscrizione di {user.nome.split(" ")[0]} al portale pubblico A.C.D.T.
              </div>
            </div>
            <div data-testid="tessera-qrcode" className="h-24 w-24 rounded-lg bg-white p-2 flex items-center justify-center flex-shrink-0">
              <QRCodeSVG value={verifyUrl} size={88} level="M" bgColor="#ffffff" fgColor="#0a1f55" includeMargin={false} />
            </div>
          </div>
          <div className="mt-3 text-[10px] text-slate-400 text-center break-all">{verifyUrl}</div>
        </div>
      </div>
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,application/pdf" onChange={handleFotoChange} className="hidden" data-testid="tessera-foto-input" />
      <div className="no-print grid grid-cols-3 gap-3 mt-4">
        <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="btn-cambia-foto" variant="outline" className="h-12 border-[#102a6b]/30 text-[#102a6b] hover:bg-[#102a6b]/5 font-bold">
          <Camera className="mr-2 h-4 w-4" /> {uploading ? "Caricamento…" : fotoUrl ? "Cambia foto" : "Carica foto"}
        </Button>
        <Button onClick={handlePrint} data-testid="btn-stampa-tessera" variant="outline" className="h-12 border-[#102a6b]/30 text-[#102a6b] hover:bg-[#102a6b]/5 font-bold">
          <FileText className="mr-2 h-4 w-4" /> Stampa
        </Button>
        <Button onClick={handleScaricaPDF} data-testid="btn-scarica-pdf-tessera" className="h-12 bg-[#102a6b] hover:bg-[#0a1f55] text-white font-bold">
          <Download className="mr-2 h-4 w-4" /> PDF
        </Button>
      </div>
    </div>
  );
}

function CFDTTab({ user, cfdtPercent }) {
  const [corsi, setCorsi] = useState([]);
  useEffect(() => {
    axios.get(`${API}/api/me/corsi`, { withCredentials: true })
      .then((r) => setCorsi(r.data.filter((c) => c.completato)))
      .catch(() => setCorsi([]));
  }, []);
  return (
    <>
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <h3 className="text-xl font-extrabold text-[#0a1f55]">I tuoi crediti CFDT</h3>
        <div className="mt-4 flex items-end gap-4">
          <div className="text-6xl font-extrabold text-[#102a6b] leading-none">{user.cfdtAnno}</div>
          <div className="pb-2"><div className="text-2xl font-bold text-slate-400">/ {user.cfdtRichiesti}</div><div className="text-[12px] text-slate-500">richiesti annualmente</div></div>
        </div>
        <Progress value={cfdtPercent} className="h-4 mt-5" />
        <div className="mt-4 grid sm:grid-cols-3 gap-3">
          <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3"><div className="text-[10.5px] uppercase tracking-wider text-emerald-700 font-bold">Accumulati</div><div className="text-2xl font-extrabold text-emerald-700">{user.cfdtAnno}</div></div>
          <div className="rounded-xl bg-[#f59e0b]/10 border border-[#f59e0b]/30 p-3"><div className="text-[10.5px] uppercase tracking-wider text-[#b45309] font-bold">Mancanti</div><div className="text-2xl font-extrabold text-[#b45309]">{Math.max(0, user.cfdtRichiesti - user.cfdtAnno)}</div></div>
          <div className="rounded-xl bg-[#102a6b]/10 border border-[#102a6b]/30 p-3"><div className="text-[10.5px] uppercase tracking-wider text-[#102a6b] font-bold">Progresso</div><div className="text-2xl font-extrabold text-[#102a6b]">{cfdtPercent}%</div></div>
        </div>
      </div>
      <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-6">
        <h3 className="text-lg font-extrabold text-[#0a1f55] mb-3">Storico CFDT</h3>
        {corsi.length === 0 ? (
          <div className="text-[13px] text-slate-400 text-center py-6">Nessun corso completato ancora. Iscriviti al Catalogo Corsi per iniziare.</div>
        ) : (
          <div className="space-y-2">
            {corsi.map((c) => (
              <div key={c.id} className="flex items-center justify-between gap-3 p-3 rounded-lg bg-slate-50">
                <div><div className="font-bold text-[13.5px] text-[#0a1f55]">{c.titolo}</div><div className="text-[11px] text-slate-500">{c.data_fine ? new Date(c.data_fine).toLocaleDateString("it-IT") : ""}</div></div>
                <span className="px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-700 text-[11px] font-bold">+{c.cfdt} CFDT</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function CorsiTab({ onOpen }) {
  const [corsi, setCorsi] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios.get(`${API}/api/me/corsi`, { withCredentials: true })
      .then((r) => setCorsi(r.data))
      .catch(() => setCorsi([]))
      .finally(() => setLoading(false));
  }, []);
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-extrabold text-[#0a1f55]">I miei Corsi</h2>
          <p className="text-[13.5px] text-slate-500">{corsi.length} cors{corsi.length === 1 ? "o" : "i"} attiv{corsi.length === 1 ? "o" : "i"} nel tuo account</p>
        </div>
      </div>
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#102a6b]" /></div>
      ) : corsi.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center">
          <GraduationCap className="h-10 w-10 mx-auto text-slate-300 mb-3" />
          <h3 className="font-bold text-[#0a1f55]">Nessun corso ancora</h3>
          <p className="text-[13px] text-slate-500 mt-1">Esplora il catalogo per iscriverti al tuo primo corso.</p>
        </div>
      ) : corsi.map((c) => (
        <div key={c.id} data-testid={`corso-${c.id}`} className="rounded-2xl border border-slate-200 bg-white p-5 flex flex-col md:flex-row md:items-center gap-4 hover:border-[#102a6b]/40 transition-colors">
          <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${c.completato ? "bg-emerald-500/15 text-emerald-700" : "bg-[#f59e0b]/15 text-[#b45309]"}`}>
            {c.completato ? <CheckCircle2 className="h-5 w-5" /> : <PlayCircle className="h-5 w-5" />}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-[#0a1f55]">{c.titolo}</h3>
            <div className="text-[12.5px] text-slate-500 mt-0.5">
              {c.ore} ore • {c.lezioni?.length || 0} lezioni • {c.cfdt} CFDT
            </div>
            <div className="mt-2 flex items-center gap-2">
              <Progress value={c.progresso} className="h-2 flex-1" />
              <span className="text-[11.5px] font-bold text-[#102a6b] min-w-[36px] text-right">{c.progresso}%</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {c.completato && (
              <Button variant="outline" className="border-slate-300 text-[#102a6b]"><Download className="mr-2 h-4 w-4" /> Attestato</Button>
            )}
            <Button onClick={() => onOpen(c)} className="bg-[#102a6b] hover:bg-[#0a1f55] text-white">
              {c.completato ? "Rivedi" : "Continua"} <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

function CorsoPlayer({ corso: initial, onBack }) {
  const [corso, setCorso] = useState(initial);
  const [activeLezId, setActiveLezId] = useState(initial.lezioni.find(l => !l.completata)?.id || initial.lezioni[0].id);
  const [completing, setCompleting] = useState(false);
  const [quizOpen, setQuizOpen] = useState(false);
  const activeLez = corso.lezioni.find(l => l.id === activeLezId);
  const lezioneIndex = corso.lezioni.findIndex(l => l.id === activeLezId);

  const refresh = async () => {
    try {
      const { data } = await axios.get(`${API}/api/me/corsi`, { withCredentials: true });
      const updated = data.find((c) => c.id === corso.id);
      if (updated) setCorso(updated);
    } catch (_e) { /* ignore */ }
  };

  const allLessonsDone = corso.lezioni.every((l) => l.completata);
  const quizPassed = corso.quiz?.superato;
  const attestatoSbloccato = corso.attestato_sbloccato;

  const handleComplete = async () => {
    if (activeLez.completata || completing) return;
    setCompleting(true);
    try {
      const { data } = await axios.post(
        `${API}/api/me/corsi/${corso.id}/lezioni/${activeLez.id}/completa`,
        {},
        { withCredentials: true }
      );
      await refresh();
      if (data.completato && data.cfdt_awarded > 0) {
        alert(`🎉 Corso completato! Hai guadagnato +${data.cfdt_awarded} CFDT`);
      }
      // Vai automaticamente alla prossima lezione non completata
      const next = corso.lezioni.find((l) => !l.completata && l.id !== activeLezId);
      if (next) setActiveLezId(next.id);
    } catch (e) {
      alert(e.response?.data?.detail || "Errore");
    } finally {
      setCompleting(false);
    }
  };

  return (
    <>
      <button onClick={onBack} className="inline-flex items-center gap-2 text-[13px] font-semibold text-[#102a6b] hover:underline mb-4" data-testid="corso-back-btn">
        <ArrowLeft className="h-4 w-4" /> Torna ai corsi
      </button>

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Player */}
        <div className="lg:col-span-2 space-y-5">
          <div className="rounded-2xl overflow-hidden border border-slate-200 bg-[#0a1f55] aspect-video relative flex items-center justify-center">
            <div className="absolute inset-0 bg-gradient-to-br from-[#0a1f55] via-[#102a6b] to-[#1e3a8a]" />
            <div className="absolute -top-16 -right-16 h-44 w-44 rounded-full bg-[#f59e0b]/25 blur-3xl" />
            <button className="relative z-10 h-20 w-20 rounded-full bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1f55] flex items-center justify-center shadow-2xl transition-transform hover:scale-105">
              <Play className="h-8 w-8 ml-1" fill="currentColor" />
            </button>
            <div className="absolute bottom-4 left-4 right-4 text-white">
              <div className="text-[11px] uppercase tracking-wider text-[#fbbf24] font-bold">Lezione {lezioneIndex + 1} di {corso.lezioni.length}</div>
              <div className="text-lg font-extrabold mt-0.5">{activeLez.titolo}</div>
            </div>
          </div>

          {/* Pulsante completa lezione */}
          {!corso.completato && (
            <Button
              data-testid="btn-completa-lezione"
              onClick={handleComplete}
              disabled={activeLez.completata || completing}
              className={`w-full h-12 font-bold ${activeLez.completata ? "bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/15 cursor-default" : "bg-emerald-600 hover:bg-emerald-700 text-white"}`}
            >
              {completing ? <Loader2 className="h-4 w-4 animate-spin" /> :
                activeLez.completata ? <><CheckCircle2 className="mr-2 h-4 w-4" /> Lezione completata</> :
                <><CheckCircle2 className="mr-2 h-4 w-4" /> Marca come completata</>}
            </Button>
          )}

          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <h2 className="text-2xl font-extrabold text-[#0a1f55]">{corso.titolo}</h2>
            <p className="text-[13.5px] text-slate-600 mt-2">{corso.descrizione}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Pill icon={Clock} text={`${corso.ore} ore`} />
              <Pill icon={GraduationCap} text={`${corso.lezioni.length} lezioni`} />
              <Pill icon={Award} text={`${corso.cfdt} CFDT`} />
              <Pill icon={FileText} text={`${corso.materiali.length} materiali`} />
            </div>
          </div>

          {/* Materiali */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <h3 className="text-lg font-extrabold text-[#0a1f55] mb-3">Materiali scaricabili</h3>
            <div className="space-y-2">
              {corso.materiali.map((m) => (
                <div key={m.id} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                  <div className="h-10 w-10 rounded-lg bg-[#102a6b]/10 text-[#102a6b] flex items-center justify-center"><FileText className="h-5 w-5" /></div>
                  <div className="flex-1">
                    <div className="font-bold text-[14px] text-[#0a1f55]">{m.nome}</div>
                    <div className="text-[11.5px] text-slate-500">PDF • {m.size}</div>
                  </div>
                  <button className="h-9 w-9 rounded-lg bg-[#102a6b] hover:bg-[#0a1f55] text-white flex items-center justify-center"><Download className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </div>

          {/* Quiz */}
          <div className={`rounded-2xl border-2 p-6 ${quizPassed ? "border-emerald-300 bg-emerald-50" : "border-[#f59e0b]/40 bg-[#f59e0b]/5"}`}>
            <div className="flex items-start gap-4">
              <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${quizPassed ? "bg-emerald-500 text-white" : "bg-[#f59e0b] text-[#0a1f55]"}`}>
                <FileQuestion className="h-6 w-6" />
              </div>
              <div className="flex-1">
                <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Quiz finale</div>
                <h3 className="text-lg font-extrabold text-[#0a1f55]">Test di valutazione</h3>
                <p className="text-[13px] text-slate-600 mt-1">5 domande a risposta multipla. Punteggio minimo: 60%.</p>
                {quizPassed ? (
                  <div className="mt-3 inline-flex items-center gap-2 text-[13px] font-bold text-emerald-700">
                    <CheckCircle2 className="h-4 w-4" /> Superato il {new Date(corso.quiz.data_completamento).toLocaleDateString("it-IT")} con {corso.quiz.punteggio}%
                  </div>
                ) : (
                  <Button
                    data-testid="btn-avvia-quiz"
                    disabled={!allLessonsDone}
                    onClick={() => setQuizOpen(true)}
                    className="mt-3 bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1f55] font-bold disabled:opacity-50"
                  >
                    {!allLessonsDone ? <><Lock className="mr-2 h-4 w-4" /> Completa tutte le lezioni</> : <>Avvia quiz <ArrowRight className="ml-2 h-4 w-4" /></>}
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Attestato - solo se quiz superato */}
          {attestatoSbloccato && (
            <div className="rounded-2xl bg-gradient-to-br from-[#102a6b] to-[#1e3a8a] text-white p-6 flex flex-col md:flex-row md:items-center gap-5">
              <div className="h-14 w-14 rounded-2xl bg-[#f59e0b] text-[#0a1f55] flex items-center justify-center"><Award className="h-7 w-7" /></div>
              <div className="flex-1">
                <div className="text-[11px] uppercase tracking-wider font-bold text-[#fbbf24]">Attestato sbloccato</div>
                <h3 className="text-lg font-extrabold">Hai superato il quiz finale!</h3>
                <p className="text-[13px] text-slate-300 mt-0.5">Scarica il tuo attestato ufficiale A.C.D.T. o condividilo su LinkedIn.</p>
              </div>
              <div className="flex gap-2">
                <Button asChild className="bg-white hover:bg-slate-100 text-[#102a6b] font-bold">
                  <a href={`${API}/api/me/corsi/${corso.id}/attestato`} target="_blank" rel="noopener noreferrer" data-testid="btn-download-attestato">
                    <Download className="mr-2 h-4 w-4" /> Scarica
                  </a>
                </Button>
                <Button asChild className="bg-[#0a66c2] hover:bg-[#004182] text-white font-bold">
                  <a
                    href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.origin + '/registro-nazionale')}&summary=${encodeURIComponent(`Ho completato il corso "${corso.titolo}" certificato A.C.D.T. con ${corso.quiz?.punteggio || 0}% e guadagnato ${corso.cfdt} CFDT! 🎓`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="btn-share-linkedin"
                  >
                    <Sparkles className="mr-2 h-4 w-4" /> Condividi su LinkedIn
                  </a>
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar lezioni */}
        <div className="rounded-2xl border border-slate-200 bg-white p-4 h-fit lg:sticky lg:top-20">
          <div className="px-2 py-2 border-b border-slate-200 mb-2">
            <h3 className="font-extrabold text-[#0a1f55]">Programma del corso</h3>
            <div className="text-[12px] text-slate-500 mt-0.5">{corso.lezioni.filter(l => l.completata).length}/{corso.lezioni.length} lezioni completate</div>
          </div>
          <div className="space-y-1">
            {corso.lezioni.map((l, i) => {
              const active = l.id === activeLezId;
              return (
                <button
                  key={l.id}
                  onClick={() => setActiveLezId(l.id)}
                  className={`w-full text-left p-3 rounded-lg flex items-start gap-3 transition-colors ${active ? "bg-[#102a6b]/8 border border-[#102a6b]/20" : "hover:bg-slate-50 border border-transparent"}`}
                >
                  <div className={`h-7 w-7 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0 ${l.completata ? "bg-emerald-500 text-white" : active ? "bg-[#f59e0b] text-[#0a1f55]" : "bg-slate-200 text-slate-600"}`}>
                    {l.completata ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className={`text-[13px] font-semibold leading-tight ${active ? "text-[#0a1f55]" : "text-slate-700"}`}>{l.titolo}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">{l.durata}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {quizOpen && (
        <QuizModal
          enrollmentId={corso.id}
          onClose={() => setQuizOpen(false)}
          onPassed={() => { refresh(); }}
        />
      )}
    </>
  );
}

function Pill({ icon: Icon, text }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-[12px] font-semibold">
      <Icon className="h-3.5 w-3.5" /> {text}
    </span>
  );
}

function CatalogoTab({ user }) {
  const [filtro, setFiltro] = useState("tutti");
  const [search, setSearch] = useState("");
  const [catalogo, setCatalogo] = useState([]);
  const [loading, setLoading] = useState(true);
  const [iscritti, setIscritti] = useState(new Set());
  const [enrolling, setEnrolling] = useState(null);
  const isAdminOrDirettivo = user.role === "admin" || !!user.ruolo_direttivo || !!user.accesso_pieno;
  const isPremium = user.pacchetto === "Premium" || isAdminOrDirettivo;

  const load = async () => {
    setLoading(true);
    try {
      const [cat, mine] = await Promise.all([
        axios.get(`${API}/api/catalogo`),
        axios.get(`${API}/api/me/corsi`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setCatalogo(cat.data);
      setIscritti(new Set(mine.data.map((c) => c.corso_id)));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const handleIscriviti = async (corso) => {
    setEnrolling(corso.id);
    try {
      await axios.post(`${API}/api/me/corsi/iscriviti`, { corso_id: corso.id }, { withCredentials: true });
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || "Errore iscrizione");
    } finally {
      setEnrolling(null);
    }
  };

  const filtered = catalogo.filter((c) => {
    if (filtro === "gratuiti" && !(c.gratis_premium && isPremium)) return false;
    if (filtro === "pagamento" && (c.gratis_premium && isPremium)) return false;
    if (filtro === "pagamento" && c.prezzo === 0) return false;
    const q = search.toLowerCase();
    return !q || [c.titolo, c.categoria, c.descrizione].some((v) => (v || "").toLowerCase().includes(q));
  });

  return (
    <>
      {/* Hero catalogo */}
      <div className="rounded-2xl bg-gradient-to-br from-[#0a1f55] via-[#102a6b] to-[#1e3a8a] text-white p-6 md:p-8 relative overflow-hidden">
        <div className="absolute -top-16 -right-16 h-44 w-44 rounded-full bg-[#f59e0b]/25 blur-3xl" />
        <div className="relative max-w-2xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#f59e0b] text-[#0a1f55] text-[11px] font-extrabold uppercase tracking-wider">
            <Sparkles className="h-3 w-3" /> Catalogo Corsi
          </div>
          <h2 className="mt-3 text-2xl md:text-3xl font-extrabold">Cresci con i corsi A.C.D.T.</h2>
          <p className="mt-2 text-slate-200 text-[14px]">
            {isPremium ? (
              <>Con il tuo pacchetto <strong className="text-[#fbbf24]">Premium</strong>, alcuni corsi sono già inclusi e gratuiti.</>
            ) : (
              <>Esplora i corsi disponibili e arricchisci la tua formazione professionale.</>
            )}
          </p>
        </div>
      </div>

      {/* Search + filtri */}
      <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca un corso..." className="pl-10 h-11" />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] uppercase tracking-wider font-bold text-slate-500 hidden md:inline-flex items-center gap-1"><Filter className="h-3.5 w-3.5" /> Filtra</span>
          {[
            { id: "tutti", l: "Tutti" },
            { id: "gratuiti", l: "Gratis Premium" },
            { id: "pagamento", l: "A pagamento" },
          ].map((f) => (
            <button
              key={f.id}
              data-testid={`filtro-${f.id}`}
              onClick={() => setFiltro(f.id)}
              className={`px-3 py-2 rounded-lg text-[12.5px] font-bold transition-colors ${filtro === f.id ? "bg-[#102a6b] text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-[#102a6b]/40"}`}
            >
              {f.l}
            </button>
          ))}
        </div>
      </div>

      {/* Grid corsi */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#102a6b]" /></div>
      ) : (
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {/* Corso AI ACT Literacy — in arrivo */}
        <a href="/corsi/ai-act-literacy" target="_blank" rel="noreferrer" data-testid="catalogo-corso-ai-act" className="rounded-2xl border-2 border-[#f59e0b]/50 bg-white overflow-hidden hover:shadow-lg transition-all">
          <div className="aspect-[16/10] bg-slate-100 relative overflow-hidden">
            <img src="https://static.prod-images.emergentagent.com/jobs/b32a9d06-cd1a-4936-8a74-34f0d65c7ad3/images/8bc51dc9b2a64cad9a38e4ae0990a6e513f718ad8cb24e83eac6fe47f2644581.jpeg" alt="AI ACT Literacy" className="w-full h-full object-cover" />
            <div className="absolute top-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/95 backdrop-blur text-[10.5px] font-extrabold uppercase tracking-wider text-[#0a1f55]">Alta formazione</div>
            <div className="absolute top-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#f59e0b] text-[#0a1f55] text-[10.5px] font-extrabold uppercase tracking-wider">In arrivo</div>
          </div>
          <div className="p-5">
            <h3 className="font-extrabold text-[#0a1f55] leading-tight line-clamp-2 min-h-[2.7rem]">Corso di alta formazione in alfabetizzazione AI ACT Literacy base</h3>
            <p className="text-[12.5px] text-slate-600 mt-1.5 line-clamp-2 min-h-[2.5rem]">L'obbligo di AI literacy previsto dall'art. 4 del Regolamento UE 2024/1689 (AI Act), in vigore dal 2 febbraio 2025.</p>
            <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between gap-3">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">Scopri di più</div>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#f59e0b]/15 text-[#b45309] text-[12px] font-extrabold uppercase tracking-wider"><Clock className="h-3.5 w-3.5" /> In arrivo</span>
            </div>
          </div>
        </a>
        {filtered.map((c) => {
          const gratis = (c.gratis_premium && isPremium) || c.prezzo === 0;
          const alreadyEnrolled = iscritti.has(c.id);
          return (
            <div key={c.id} data-testid={`catalogo-corso-${c.id}`} className="rounded-2xl border border-slate-200 bg-white overflow-hidden hover:border-[#102a6b]/40 hover:shadow-lg transition-all">
              <div className="aspect-[16/10] bg-slate-100 relative overflow-hidden">
                {c.immagine && <img src={c.immagine} alt={c.titolo} className="w-full h-full object-cover" />}
                <div className="absolute top-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/95 backdrop-blur text-[10.5px] font-extrabold uppercase tracking-wider text-[#0a1f55]">
                  {c.categoria}
                </div>
                {gratis && (
                  <div className="absolute top-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#f59e0b] text-[#0a1f55] text-[10.5px] font-extrabold uppercase tracking-wider">
                    In arrivo
                  </div>
                )}
              </div>
              <div className="p-5">
                <h3 className="font-extrabold text-[#0a1f55] leading-tight line-clamp-2 min-h-[2.7rem]">{c.titolo}</h3>
                <p className="text-[12.5px] text-slate-600 mt-1.5 line-clamp-2 min-h-[2.5rem]">{c.descrizione}</p>
                <div className="mt-3 flex items-center gap-3 text-[11.5px] text-slate-500">
                  <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {c.ore}h</span>
                  <span className="inline-flex items-center gap-1"><Award className="h-3.5 w-3.5" /> {c.cfdt} CFDT</span>
                  <span className="inline-flex items-center gap-1"><Star className="h-3.5 w-3.5 fill-[#f59e0b] text-[#f59e0b]" /> {c.rating}</span>
                </div>
                <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between gap-3">
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">Disponibilità</div>
                  <span data-testid={`btn-iscrivi-${c.id}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#f59e0b]/15 text-[#b45309] text-[12px] font-extrabold uppercase tracking-wider">
                    <Clock className="h-3.5 w-3.5" /> In arrivo
                  </span>
                </div>
              </div>
            </div>
          );
        })}
        {!loading && filtered.length === 0 && (
          <div className="col-span-full text-center py-12 text-slate-400 text-[13px]">Nessun corso trovato</div>
        )}
      </div>
      )}

      <div className="rounded-xl bg-[#102a6b]/5 border border-[#102a6b]/15 p-4 text-[12.5px] text-[#102a6b]">
        📅 <strong>NOTA:</strong> i corsi del catalogo saranno disponibili a breve. Nel frattempo puoi seguire la <strong>Formazione A.C.D.T.</strong> dal menu laterale.
      </div>
    </>
  );
}

function DocsTab({ docs }) {
  const [pubDocs, setPubDocs] = useState([]);
  const [loadingPub, setLoadingPub] = useState(true);
  useEffect(() => {
    axios.get(`${API}/api/me/documenti-pubblici`, { withCredentials: true })
      .then((r) => setPubDocs(r.data || []))
      .catch(() => setPubDocs([]))
      .finally(() => setLoadingPub(false));
  }, []);
  const formatBytes = (b) => b < 1024 * 1024 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`;
  return (
    <div className="space-y-6">
      {/* Documenti pubblici ufficiali */}
      {!loadingPub && pubDocs.length > 0 && (
        <div>
          <h3 className="font-extrabold text-[#0a1f55] mb-3">Documenti ufficiali A.C.D.T.</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            {pubDocs.map((d) => (
              <a
                key={d.id}
                href={`${API}/api/me/corso-abilitazione/materiali/${d.id}/download`}
                target="_blank" rel="noreferrer"
                data-testid={`doc-pubblico-${d.id}`}
                className="rounded-2xl border border-[#f59e0b]/40 bg-gradient-to-br from-[#fff7ed] to-white p-5 flex items-center gap-4 hover:shadow-md hover:border-[#f59e0b] transition-all"
              >
                <div className="h-12 w-12 rounded-xl bg-[#f59e0b]/15 text-[#b45309] flex items-center justify-center flex-shrink-0">
                  <Award className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-extrabold text-[#0a1f55] text-[14px] leading-tight">{d.titolo}</div>
                  {d.descrizione && <div className="text-[11.5px] text-slate-600 mt-0.5 line-clamp-2">{d.descrizione}</div>}
                  <div className="text-[11px] text-slate-500 mt-1">PDF • {formatBytes(d.size)}</div>
                </div>
                <div className="h-10 w-10 rounded-lg bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1020] flex items-center justify-center flex-shrink-0">
                  <Download className="h-4 w-4" />
                </div>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Documenti personali del socio (mock) */}
      {docs.length > 0 && (
        <div>
          <h3 className="font-extrabold text-[#0a1f55] mb-3">I tuoi documenti</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            {docs.map((d) => {
              const I = ICONS[d.icon] || FileText;
              return (
                <div key={d.id} className="rounded-2xl border border-slate-200 bg-white p-5 flex items-center gap-4 hover:border-[#102a6b]/40 transition-colors">
                  <div className="h-12 w-12 rounded-xl bg-[#102a6b]/10 text-[#102a6b] flex items-center justify-center"><I className="h-5 w-5" /></div>
                  <div className="flex-1">
                    <div className="font-bold text-[#0a1f55] text-[14.5px] leading-tight">{d.nome}</div>
                    <div className="text-[11.5px] text-slate-500 mt-0.5">{d.tipo} • {d.size}</div>
                  </div>
                  <button className="h-10 w-10 rounded-lg bg-[#102a6b] hover:bg-[#0a1f55] text-white flex items-center justify-center"><Download className="h-4 w-4" /></button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function PagamentiTab() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios.get(`${API}/api/me/payments`, { withCredentials: true })
      .then((r) => setPayments(r.data))
      .catch(() => setPayments([]))
      .finally(() => setLoading(false));
  }, []);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6">
      <h3 className="text-lg font-extrabold text-[#0a1f55] mb-4">Storico Pagamenti</h3>
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-[#102a6b]" /></div>
      ) : payments.length === 0 ? (
        <div className="text-[13px] text-slate-400 text-center py-8">Nessun pagamento ancora registrato</div>
      ) : (
        <div className="space-y-2">
          {payments.map((p) => (
            <div key={p.id} className="flex items-center justify-between gap-3 p-4 rounded-xl border border-slate-200">
              <div className="h-10 w-10 rounded-lg bg-emerald-500/15 text-emerald-700 flex items-center justify-center"><CheckCircle2 className="h-5 w-5" /></div>
              <div className="flex-1">
                <div className="font-bold text-[14px] text-[#0a1f55]">{p.descrizione}</div>
                <div className="text-[12px] text-slate-500">{new Date(p.data).toLocaleDateString("it-IT")} • {p.metodo}</div>
              </div>
              <div className="text-right">
                <div className="font-extrabold text-[#0a1f55]">€ {p.importo.toFixed(2)}</div>
                <button className="text-[11px] text-[#102a6b] font-bold hover:underline">Scarica ricevuta</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ProfiloTab({ user }) {
  const fields = [
    { l: "Nome", v: user.nome }, { l: "Cognome", v: user.cognome },
    { l: "Email", v: user.email }, { l: "Telefono", v: user.telefono },
    { l: "Codice Fiscale", v: user.codiceFiscale }, { l: "N. Tessera", v: user.numeroTessera },
  ];
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 max-w-3xl">
      <div className="flex items-center gap-4 pb-6 border-b border-slate-200">
        <div className="h-20 w-20 rounded-full bg-gradient-to-br from-[#102a6b] to-[#1e40af] text-white flex items-center justify-center text-2xl font-extrabold">
          {user.nome[0]}{user.cognome[0]}
        </div>
        <div>
          <h3 className="text-2xl font-extrabold text-[#0a1f55]">{user.nome} {user.cognome}</h3>
          <div className="text-slate-500">{user.email}</div>
        </div>
      </div>
      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        {fields.map((f) => (
          <div key={f.l}>
            <div className="text-[11.5px] uppercase tracking-wider text-slate-500 font-bold">{f.l}</div>
            <div className="mt-1 text-[#0a1f55] font-semibold">{f.v}</div>
          </div>
        ))}
      </div>
      <Button className="mt-6 bg-[#102a6b] hover:bg-[#0a1f55] text-white"><Settings className="mr-2 h-4 w-4" /> Modifica dati</Button>
    </div>
  );
}

// ============================================================================
// CORSI DI SPECIALIZZAZIONE — In arrivo
// ============================================================================
const SPECIALIZZAZIONI = [
  {
    id: "consulente-digitale-tecnologico",
    titolo: "Consulente Digitale Tecnologico",
    descrizione: "Diventa il punto di riferimento aziendale per la digitalizzazione tecnologica.",
    img: "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: "consulente-software-trading",
    titolo: "Consulente Software in Abbonamento Trading",
    descrizione: "Strumenti, software e best practice per il trading professionale.",
    img: "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: "consulente-editoriale-marketing",
    titolo: "Consulente Editoriale Digitale e Marketing",
    descrizione: "Editoria digitale, content strategy e marketing per editori e brand.",
    img: "https://images.unsplash.com/photo-1432888622747-4eb9a8f5a07d?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: "consulente-innovazione",
    titolo: "Consulente in Innovazione",
    descrizione: "Innovation management, design thinking e processi di trasformazione.",
    img: "https://images.unsplash.com/photo-1559136555-9303baea8ebd?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: "consulente-eco-sostenibilita",
    titolo: "Consulente Eco-Sostenibilità / Bio-Sostenibilità",
    descrizione: "ESG, bilanci di sostenibilità e strategie ambientali certificate.",
    img: "https://images.unsplash.com/photo-1497436072909-60f360e1d4b1?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: "consulente-promozione-ia",
    titolo: "Consulente in Promozione e Sviluppo IA",
    descrizione: "Intelligenza artificiale applicata a marketing, automazione e strategia.",
    img: "https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: "consulente-ecommerce",
    titolo: "Consulente Ecommerce / Shop / Dropshipping",
    descrizione: "Avvio e gestione di shop online, dropshipping e marketplace.",
    img: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: "consulente-viaggio-online",
    titolo: "Consulente di Viaggio Online",
    descrizione: "Travel agent digitale: piattaforme, booking e consulenza personalizzata.",
    img: "https://images.unsplash.com/photo-1488646953014-85cb44e25828?auto=format&fit=crop&w=800&q=80",
  },
];

const SPEC_CFDT = 20;
const SPEC_PREZZO_LISTINO = 179;
const SPEC_PREZZO_PROMO = 149;

function SpecializzazioniTab() {
  const [interesseInviato, setInteresseInviato] = useState({});

  const segnalaInteresse = (id) => {
    setInteresseInviato((prev) => ({ ...prev, [id]: true }));
    setTimeout(() => {
      setInteresseInviato((prev) => ({ ...prev, [id]: false }));
    }, 3500);
  };

  return (
    <div data-testid="specializzazioni-tab" className="space-y-6">
      {/* Hero */}
      <div className="rounded-3xl bg-gradient-to-br from-[#0a1f55] via-[#102a6b] to-[#1e3a8a] text-white p-7 md:p-10 relative overflow-hidden">
        <div className="absolute -top-20 -right-20 h-72 w-72 rounded-full bg-[#f59e0b]/20 blur-3xl"></div>
        <div className="absolute -bottom-20 -left-20 h-72 w-72 rounded-full bg-white/10 blur-3xl"></div>
        <div className="relative">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#f59e0b] text-[#0a1f55] text-[10.5px] font-extrabold uppercase tracking-[0.2em]">
            <Sparkles className="h-3 w-3" /> In arrivo · Specializzazioni
          </div>
          <h2 className="mt-4 text-3xl md:text-4xl font-extrabold leading-tight">
            Corsi di Specializzazione
          </h2>
          <p className="mt-3 text-slate-200 text-[14.5px] max-w-2xl">
            Percorsi verticali da <strong>20 CFDT</strong> ciascuno per diventare un consulente
            esperto nel tuo settore di riferimento. Stiamo finalizzando contenuti e date —
            puoi già segnalare il tuo interesse e prenotare il posto a prezzo lancio.
          </p>
          <div className="mt-5 flex items-baseline gap-2 flex-wrap">
            <span className="text-2xl font-bold text-slate-300 line-through">€ {SPEC_PREZZO_LISTINO}</span>
            <span className="text-5xl font-extrabold text-[#fbbf24]">€ {SPEC_PREZZO_PROMO}</span>
            <span className="text-sm text-slate-300">/ una tantum per ogni specializzazione</span>
            <span className="ml-2 px-2.5 py-1 rounded-full bg-red-600 text-white text-[10.5px] font-extrabold uppercase tracking-wider">-30€ lancio</span>
          </div>
          <div className="mt-3 inline-flex items-center gap-2 text-[12.5px] font-bold text-[#fbbf24] uppercase tracking-wider">
            <TrendingUp className="h-3.5 w-3.5" /> {SPEC_CFDT} crediti CFDT per ciascun corso
          </div>
        </div>
      </div>

      {/* Grid corsi */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {SPECIALIZZAZIONI.map((s) => (
          <div
            key={s.id}
            data-testid={`spec-card-${s.id}`}
            className="rounded-2xl border border-slate-200 bg-white overflow-hidden hover:border-[#102a6b]/40 hover:shadow-xl transition-all flex flex-col"
          >
            <div className="relative h-44 bg-gradient-to-br from-[#102a6b] to-[#1e40af] overflow-hidden">
              <img
                src={s.img}
                alt={s.titolo}
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent"></div>
              <div className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#f59e0b] text-[#0a1f55] text-[10px] font-extrabold uppercase tracking-wider">
                <Sparkles className="h-3 w-3" /> In arrivo
              </div>
              <div className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-600 text-white text-[10px] font-extrabold uppercase tracking-wider">
                -30€
              </div>
              <div className="absolute bottom-3 left-3 right-3">
                <h3 className="text-white font-extrabold text-[16px] leading-tight drop-shadow-lg">{s.titolo}</h3>
              </div>
            </div>

            <div className="p-5 flex-1 flex flex-col">
              <p className="text-[13px] text-slate-600 leading-relaxed">{s.descrizione}</p>

              <div className="mt-4 grid grid-cols-2 gap-2 text-center">
                <div className="rounded-lg bg-slate-50 border border-slate-200 py-2">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">CFDT</div>
                  <div className="text-lg font-extrabold text-[#102a6b]">{SPEC_CFDT}</div>
                </div>
                <div className="rounded-lg bg-slate-50 border border-slate-200 py-2">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Prezzo lancio</div>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-[11px] line-through text-slate-400 font-bold">€{SPEC_PREZZO_LISTINO}</span>
                    <span className="text-lg font-extrabold text-red-600">€{SPEC_PREZZO_PROMO}</span>
                  </div>
                </div>
              </div>

              <div className="mt-4 mt-auto pt-4">
                {interesseInviato[s.id] ? (
                  <div className="h-11 rounded-lg bg-green-100 text-green-800 font-bold text-[13px] flex items-center justify-center gap-2" data-testid={`spec-interesse-ok-${s.id}`}>
                    <Check className="h-4 w-4" /> Interesse registrato — ti avviseremo
                  </div>
                ) : (
                  <Button
                    onClick={() => segnalaInteresse(s.id)}
                    data-testid={`spec-acquista-${s.id}`}
                    className="w-full h-11 bg-[#102a6b] hover:bg-[#0a1f55] text-white font-bold text-[13px]"
                  >
                    <Lock className="h-3.5 w-3.5 mr-1.5" /> Prenota a € {SPEC_PREZZO_PROMO}
                  </Button>
                )}
                <div className="mt-2 text-center text-[11px] text-slate-500">
                  Posto prenotato al prezzo lancio quando il corso sarà attivo.
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


// ============================================================================
// GUEST UPSELL BANNER — visualizzato per utenti tipo_socio = "guest"
// ============================================================================
function GuestUpsellBanner({ user }) {
  const API = process.env.REACT_APP_BACKEND_URL;
  const TESSERA_URL = "/#pacchetti";
  return (
    <div data-testid="guest-banner" className="space-y-4">
      {/* Banner principale account guest */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0a1f55] via-[#102a6b] to-[#1e3a8a] text-white p-6 md:p-7">
        <div className="absolute -top-20 -right-20 h-72 w-72 rounded-full bg-[#f59e0b]/20 blur-3xl"></div>
        <div className="relative grid md:grid-cols-[1fr_auto] gap-5 items-center">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#f59e0b] text-[#0a1020] text-[10.5px] font-extrabold uppercase tracking-wider">
              <Sparkles className="h-3 w-3" /> Account Gratuito
            </div>
            <h2 className="mt-3 text-2xl md:text-3xl font-extrabold leading-tight">
              Ciao {user.nome}, sei un passo dall&apos;essere Consulente Digitale Certificato
            </h2>
            <p className="mt-2 text-slate-200 text-[14px] leading-relaxed max-w-2xl">
              Hai un account gratuito con funzionalità limitate. Per sbloccare <strong>tessera digitale, attestato corso, CFDT, Registro Nazionale</strong> e tutti i benefit, attiva uno dei nostri pacchetti.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <a
                href={TESSERA_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="guest-cta-tessera"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#f59e0b] hover:bg-[#fbbf24] text-[#0a1020] font-extrabold text-[13.5px] transition-colors"
              >
                <CreditCard className="h-4 w-4" />
                Paga Tessera € 50 con PayPal
              </a>
              <a
                href="https://assoacdt.com/corso#acquista"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="guest-cta-corso"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 text-white font-bold text-[13.5px] transition-colors"
              >
                <GraduationCap className="h-4 w-4" />
                Corso + Tessera € 149
              </a>
            </div>
          </div>
          <a
            href={`${API}/api/pdf/guida-tutela-online`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="guest-download-guide"
            className="inline-flex flex-col items-center gap-2 p-5 rounded-2xl bg-white/10 hover:bg-white/20 border-2 border-dashed border-[#f59e0b]/50 transition-colors text-center group"
          >
            <div className="h-14 w-14 rounded-2xl bg-[#f59e0b] text-[#0a1020] flex items-center justify-center group-hover:scale-110 transition-transform">
              <Download className="h-7 w-7" />
            </div>
            <div className="text-[10.5px] uppercase tracking-wider font-extrabold text-[#fbbf24]">In regalo per te</div>
            <div className="text-[13px] font-bold text-white">Tutelarsi Online<br/>in 10 Pillole</div>
            <div className="text-[10.5px] text-slate-300">PDF · ~6 KB</div>
          </a>
        </div>
      </div>

      {/* Mini grid funzioni bloccate */}
      <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { icon: IdCard, label: "Tessera Digitale", desc: "Richiede tesseramento" },
          { icon: Award, label: "Attestato Corso", desc: "Richiede corso" },
          { icon: TrendingUp, label: "Crediti CFDT", desc: "Richiede corso" },
          { icon: FileBadge2, label: "Registro Nazionale", desc: "Richiede pacchetto Premium" },
        ].map((f, i) => {
          const Icon = f.icon;
          return (
            <div key={i} className="bg-white rounded-xl border border-slate-200 p-4 opacity-70 hover:opacity-100 transition-opacity relative overflow-hidden">
              <div className="absolute top-2 right-2 text-[9px] uppercase tracking-wider font-extrabold bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">Bloccato</div>
              <Icon className="h-5 w-5 text-slate-400 mb-2" />
              <div className="text-[13px] font-bold text-[#0a1f55]">{f.label}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">{f.desc}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ============================================================================
// BACHECA SOCI — Comunicazioni direttivo + discussioni soci + link Telegram
// ============================================================================
const TELEGRAM_GROUP_URL = "https://t.me/+vsAwGpBvGzEwYWZk";

function BachecaTab({ user }) {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ titolo: "", contenuto: "", tipo: "discussione" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const isDirettivo = user?.role === "admin" || ["presidente", "vice presidente", "vicepresidente", "segretario", "consigliere", "direttore"].includes((user?.ruoloDirettivo || "").toLowerCase());

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/bacheca/posts`, { withCredentials: true });
      setPosts(r.data.posts || []);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.titolo.trim() || !form.contenuto.trim()) {
      setError("Titolo e contenuto sono obbligatori");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/api/bacheca/posts`, form, { withCredentials: true });
      setForm({ titolo: "", contenuto: "", tipo: "discussione" });
      setShowForm(false);
      load();
    } catch (e) {
      setError(e.response?.data?.detail || "Errore pubblicazione");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleLike = async (postId) => {
    try {
      await axios.post(`${API}/api/bacheca/posts/${postId}/like`, {}, { withCredentials: true });
      load();
    } catch (_) { void 0; }
  };

  const togglePin = async (postId) => {
    try {
      await axios.post(`${API}/api/bacheca/posts/${postId}/pin`, {}, { withCredentials: true });
      load();
    } catch (_) { void 0; }
  };

  const deletePost = async (postId) => {
    if (!window.confirm("Eliminare questo post?")) return;
    try {
      await axios.delete(`${API}/api/bacheca/posts/${postId}`, { withCredentials: true });
      load();
    } catch (_) { void 0; }
  };

  return (
    <div data-testid="bacheca-tab" className="space-y-6">
      {/* Header con CTA Telegram */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl md:text-3xl font-extrabold text-[#0a1f55]">Bacheca Soci</h2>
          <p className="text-[13.5px] text-slate-600 mt-1">Comunicazioni ufficiali del direttivo e discussioni tra soci.</p>
        </div>
        <a
          href={TELEGRAM_GROUP_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="bacheca-telegram-btn"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-[#0088cc] to-[#229ED9] hover:from-[#0077b3] hover:to-[#1d8bc4] text-white font-bold text-[13.5px] shadow-lg transition-all"
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.149.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.16.16-.295.295-.605.295l.213-3.054 5.56-5.022c.242-.213-.054-.334-.373-.121l-6.869 4.326-2.96-.924c-.643-.204-.657-.643.136-.953l11.566-4.458c.535-.196 1.006.128.832.829z"/></svg>
          Bacheca Telegram ACDT
        </a>
      </div>

      {/* Banner obbligatorio Telegram */}
      <div className="rounded-2xl bg-gradient-to-r from-[#0088cc]/10 to-[#229ED9]/10 border-2 border-[#0088cc]/30 p-4 md:p-5 flex items-start gap-4">
        <div className="hidden sm:flex h-12 w-12 rounded-xl bg-gradient-to-br from-[#0088cc] to-[#229ED9] text-white items-center justify-center shrink-0">
          <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.149.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.16.16-.295.295-.605.295l.213-3.054 5.56-5.022c.242-.213-.054-.334-.373-.121l-6.869 4.326-2.96-.924c-.643-.204-.657-.643.136-.953l11.566-4.458c.535-.196 1.006.128.832.829z"/></svg>
        </div>
        <div className="flex-1">
          <h3 className="font-extrabold text-[#0a1f55] text-[15px]">Iscriviti al gruppo Telegram ACDT (obbligatorio)</h3>
          <p className="text-[13px] text-slate-700 mt-1 leading-relaxed">
            Ogni socio ha l&apos;obbligo di iscriversi al gruppo ufficiale per ricevere <strong>comunicazioni urgenti, aggiornamenti del direttivo, eventi e notifiche</strong>.
          </p>
          <a
            href={TELEGRAM_GROUP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="bacheca-telegram-inline"
            className="inline-flex items-center gap-1.5 mt-2 text-[#0088cc] hover:text-[#0077b3] font-bold text-[13px] underline-offset-2 hover:underline"
          >
            Unisciti ora al gruppo Telegram →
          </a>
        </div>
      </div>

      {/* CTA nuovo post */}
      <div className="flex justify-end">
        <Button
          onClick={() => setShowForm(!showForm)}
          data-testid="bacheca-new-post-btn"
          className="bg-[#102a6b] hover:bg-[#0a1f55] text-white font-bold"
        >
          {showForm ? "Annulla" : "+ Nuovo post"}
        </Button>
      </div>

      {/* Form nuovo post */}
      {showForm && (
        <div data-testid="bacheca-form" className="bg-white rounded-2xl border-2 border-[#102a6b]/20 p-5 space-y-3">
          <h4 className="font-extrabold text-[#0a1f55]">Pubblica in bacheca</h4>
          {isDirettivo && (
            <div className="flex gap-2">
              <label className={`flex-1 cursor-pointer px-3 py-2 rounded-lg border-2 text-[12.5px] font-bold text-center transition-colors ${form.tipo === "discussione" ? "border-[#102a6b] bg-[#102a6b]/5 text-[#102a6b]" : "border-slate-200 text-slate-600"}`}>
                <input type="radio" name="tipo" value="discussione" checked={form.tipo === "discussione"} onChange={(e) => setForm({ ...form, tipo: e.target.value })} className="hidden" />
                💬 Discussione
              </label>
              <label className={`flex-1 cursor-pointer px-3 py-2 rounded-lg border-2 text-[12.5px] font-bold text-center transition-colors ${form.tipo === "comunicazione" ? "border-[#f59e0b] bg-[#f59e0b]/10 text-[#b45309]" : "border-slate-200 text-slate-600"}`}>
                <input type="radio" name="tipo" value="comunicazione" checked={form.tipo === "comunicazione"} onChange={(e) => setForm({ ...form, tipo: e.target.value })} className="hidden" />
                📢 Comunicazione ufficiale
              </label>
            </div>
          )}
          <Input value={form.titolo} onChange={(e) => setForm({ ...form, titolo: e.target.value })} placeholder="Titolo" className="h-11" data-testid="bacheca-titolo" />
          <textarea
            value={form.contenuto}
            onChange={(e) => setForm({ ...form, contenuto: e.target.value })}
            placeholder="Scrivi il tuo messaggio..."
            rows={5}
            className="w-full rounded-lg border border-slate-200 p-3 text-[13.5px] resize-none focus:border-[#102a6b] focus:outline-none"
            data-testid="bacheca-contenuto"
          />
          {error && <div className="text-red-600 text-[12.5px]">{error}</div>}
          <Button onClick={submit} disabled={submitting} className="w-full h-11 bg-[#f59e0b] hover:bg-[#fbbf24] text-[#0a1020] font-bold" data-testid="bacheca-publish-btn">
            {submitting ? "Pubblicazione..." : "Pubblica"}
          </Button>
        </div>
      )}

      {/* Lista posts */}
      {loading ? (
        <div className="text-center py-8 text-slate-500">Caricamento...</div>
      ) : posts.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-slate-200">
          <Newspaper className="h-10 w-10 text-slate-300 mx-auto mb-2" />
          <p className="text-slate-500 text-sm">Nessun post ancora. Sii il primo a pubblicare!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {posts.map((p) => {
            const isMine = p.autore_id === user?.id;
            const userLiked = (p.likes || []).includes(user?.id);
            const isComunicazione = p.tipo === "comunicazione";
            return (
              <div
                key={p.id}
                data-testid={`bacheca-post-${p.id}`}
                className={`rounded-2xl p-5 border-2 transition-all ${
                  isComunicazione
                    ? "bg-gradient-to-r from-[#fef3c7] to-white border-[#f59e0b]/40"
                    : "bg-white border-slate-200"
                } ${p.pinned ? "ring-2 ring-[#102a6b]/20" : ""}`}
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {p.pinned && <span className="text-[9.5px] uppercase tracking-wider font-extrabold bg-[#102a6b] text-white px-2 py-0.5 rounded">📌 In evidenza</span>}
                      {isComunicazione && <span className="text-[9.5px] uppercase tracking-wider font-extrabold bg-[#f59e0b] text-[#0a1020] px-2 py-0.5 rounded">📢 Direttivo</span>}
                      <span className="text-[11.5px] text-slate-500">
                        {p.autore_nome}{p.autore_ruolo_direttivo ? ` · ${p.autore_ruolo_direttivo}` : ""}
                      </span>
                      <span className="text-[11px] text-slate-400">{new Date(p.created_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}</span>
                    </div>
                    <h3 className="mt-1.5 font-extrabold text-[#0a1f55] text-[16px] leading-tight">{p.titolo}</h3>
                  </div>
                  {(isDirettivo || isMine) && (
                    <div className="flex gap-1 shrink-0">
                      {isDirettivo && (
                        <button onClick={() => togglePin(p.id)} className="p-1.5 hover:bg-slate-100 rounded text-slate-500 hover:text-[#102a6b]" title={p.pinned ? "Rimuovi pin" : "Pinna"}>
                          📌
                        </button>
                      )}
                      {(isMine || isDirettivo) && (
                        <button onClick={() => deletePost(p.id)} className="p-1.5 hover:bg-red-50 rounded text-slate-400 hover:text-red-600" title="Elimina">
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <p className="text-[13.5px] text-slate-700 leading-relaxed whitespace-pre-wrap">{p.contenuto}</p>
                <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-3">
                  <button
                    onClick={() => toggleLike(p.id)}
                    data-testid={`bacheca-like-${p.id}`}
                    className={`inline-flex items-center gap-1.5 text-[12.5px] font-semibold transition-colors ${userLiked ? "text-red-500" : "text-slate-500 hover:text-red-500"}`}
                  >
                    <span className="text-base">{userLiked ? "❤️" : "🤍"}</span>
                    {(p.likes || []).length}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
